import React, { useEffect, useLayoutEffect, useRef, useState, memo } from "react";
import { createPortal } from "react-dom";
import { useAppContext } from "./hooks/AppViewContext";

interface IBackboneWrapperProps {
    reactComponent: React.ComponentType;
    backboneView: any;
    elementId: string;
    containerId: string;
    // TODO (MYG-120374 follow-up): isZenithEnabled is always false now that ENABLE_ZENITH_PAGES is removed.
    // Remove this prop, inline the Backbone-only path, and delete the React portal branch.
    isZenithEnabled: boolean;
    elementClasses?: string[];
    insertMethod?: "prepend" | "append";
}

/**
 * Wrapper component that conditionally renders either React or Backbone view.
 */
const BackboneWrapper: React.FC<IBackboneWrapperProps> = memo(({
    // eslint-disable-next-line @typescript-eslint/naming-convention
    reactComponent: ReactComponent,
    backboneView,
    elementId,
    containerId,
    isZenithEnabled,
    elementClasses = [],
    insertMethod = "prepend"
}: IBackboneWrapperProps) => {
    const { app } = useAppContext();

    const [mountNode, setMountNode] = useState<HTMLElement | null>(null);
    const backboneViewRendered = useRef(false);

    // Track classes to manage updates without overwriting external classes (e.g. from Backbone)
    const prevClassesRef = useRef<string[]>([]);

    // Store latest classes to access in creation effect without re-triggering it
    const latestClassesRef = useRef(elementClasses);
    latestClassesRef.current = elementClasses;

    // Handle Container Lifecycle
    useEffect(() => {
        let element = document.getElementById(elementId);

        if (!element) {
            element = document.createElement("div");
            element.id = elementId;

            // Apply initial classes immediately to prevent FOUC
            const initialClasses = (latestClassesRef.current || []).filter((c) => c && c.trim() !== "");
            if (initialClasses.length > 0) {
                element.classList.add(...initialClasses);
            }
            prevClassesRef.current = initialClasses;

            const container = document.getElementById(containerId);
            if (!container) {
                throw new Error(`Container element with id "${ containerId }" not found`);
            }

            if (insertMethod === "prepend") {
                container.prepend(element);
            } else {
                container.append(element);
            }
        }

        setMountNode(element);

        return () => {
            element?.remove();
        };
    }, [elementId, containerId, insertMethod]);

    // Handle Class Updates
    useEffect(() => {
        if (!mountNode) {
            return;
        }

        const currentClasses = (elementClasses || []).filter((c) => c && c.trim() !== "");
        const prevClasses = prevClassesRef.current;

        // Efficiently sync classes
        const toRemove = prevClasses.filter((c) => currentClasses.indexOf(c) === -1);
        const toAdd = currentClasses.filter((c) => prevClasses.indexOf(c) === -1);

        if (toRemove.length > 0) {
            mountNode.classList.remove(...toRemove);
        }
        if (toAdd.length > 0) {
            mountNode.classList.add(...toAdd);
        }

        prevClassesRef.current = currentClasses;
    }, [mountNode, elementClasses]);

    // Handle View Lifecycle (Backbone vs React switching)
    useLayoutEffect(() => {
        if (!mountNode) {
            return;
        }

        if (!isZenithEnabled) {
            // Backbone Mode: Render Backbone view if not already rendered
            if (!backboneViewRendered.current) {
                void app.renderSubView(backboneView, `#${ elementId }`);
                backboneViewRendered.current = true;
            }
        }
    }, [mountNode, backboneView, app, elementId, isZenithEnabled]);

    if (isZenithEnabled && mountNode) {
        // React Mode: Clean up Backbone view if switching from Backbone to React
        if (backboneViewRendered.current) {
            // Clean up Backbone listeners without removing the DOM element
            if (backboneView.unsubscribeFromStoreEventsAndCancelPromises) {
                backboneView.unsubscribeFromStoreEventsAndCancelPromises();
            }
            backboneView.stopListening();
            backboneView.undelegateEvents();
            // Clear Backbone content before creating portal
            mountNode.innerHTML = "";
            backboneViewRendered.current = false;
        }
        return createPortal(<ReactComponent />, mountNode);
    }

    // If feature is disabled, return null - Backbone handles the view
    return null;
});

export default BackboneWrapper;